import {useMutation, useQuery, useQueryClient} from "react-query";
import ApiHelper from "./ApiHelper";
import {
    APourRole,
    AppVersion,
    EntrepriseCorpsEtat,
    Lot,
    Message,
    Operation,
    OperationElementAnnexe,
    Reserve,
    User
} from "../common/types/Types";
import {useContext, useEffect, useLayoutEffect, useRef, useState} from "react";
import {useAlert} from "react-alert";
import {AxiosError, AxiosResponse} from "axios";
import { AuthContext } from "../providers/AuthProvider";

export const useOperation = (operationId) => {
    return useQuery<Operation>(
        ["operation", operationId],
        () => ApiHelper.getOperation(operationId).then(res => res.data),
        {
            enabled: !isNaN(operationId)
            // placeholderData: {} as Operation
        }
    );
};

export const useOperationLots = (operationId) => {

    return useQuery<Lot[]>(
        ["operation", operationId, "lots"],
        () => ApiHelper.getOperationLots(operationId).then(res => res.data),
        {
            enabled: !isNaN(operationId)
            // placeholderData: []
        }
    );
};

export const useOperationReserves = (operationId) => {
    const queryClient = useQueryClient();

    return useQuery<Reserve[]>(
        ["operation", operationId, "reserves"],
        () => ApiHelper.getOperationReserves(operationId).then(res => res.data),
        {
            // placeholderData: []
            onSuccess: (reserves) => {
                const reservesByLot = reserves.reduce((obj, reserve) => {
                    if (obj[reserve.lotRefLot]) {
                        obj[reserve.lotRefLot].push(reserve);
                    } else {
                        obj[reserve.lotRefLot] = [reserve];
                    }

                    return obj;
                }, {});

                Object.keys(reservesByLot).forEach(lotRefLot => {
                    const reserves = reservesByLot[lotRefLot];

                    queryClient.setQueryData(['lot', Number(lotRefLot), 'reserves'], reserves);
                })
            }
        }
    );
};

export const useOperationElements = (operationId) => {
    return useQuery<OperationElementAnnexe[]>(
        ["operation", operationId, "elements"],
        () => ApiHelper.getOperationElements(operationId).then(res => res.data),
        {
            // placeholderData: []
        }
    );
};

export const useOperationEnterprises = (operationId) => {
    return useQuery<EntrepriseCorpsEtat[]>(
        ["operation", operationId, "enterprises"],
        () => ApiHelper.getOperationEnterprises(operationId).then(res => res.data),
        {
            enabled: !isNaN(operationId)
        }
    );
};

export const useOperationAvailableUsers = (operationId) => {
    return useQuery<User[]>(
        ["operation", operationId, "users", "available"],
        () => ApiHelper.getOperationAvailableUsers(operationId).then(res => res.data),
        {
            enabled: !isNaN(operationId)
        }
    );
};

export const useOperationUsers = (operationId) => {
    return useQuery<User[]>(
        ["operation", operationId, "users"],
        () => ApiHelper.getOperationUsers(operationId).then(res => res.data),
        {
            // placeholderData: []
        }
    );
};

export const useAddUserToOperation = () => {
    const queryClient = useQueryClient();

    return useMutation<AxiosResponse<APourRole>, AxiosError, { operationId?: number, userId: number, roleId: number }>(
        ({operationId, userId, roleId}) => {
            return ApiHelper.addUserToOperation(operationId, userId, roleId);
        },
        {
            onSuccess: async (result, variables) => {
                const {operationId} = variables;

                await queryClient.refetchQueries(['operation', operationId, 'users']);
                await queryClient.refetchQueries(['operation', operationId, 'users', 'available']);
            }
        });
};

export const useRemoveUserFromOperation = () => {
    const queryClient = useQueryClient();

    return useMutation<AxiosResponse<void>, AxiosError, { operationId?: number, aPourRoleId: number }>(
        ({operationId, aPourRoleId}) => {
            return ApiHelper.removeUserFromOperation(operationId, aPourRoleId);
        },
        {
            onSuccess: async (result, variables) => {
                const {operationId} = variables;

                await queryClient.refetchQueries(['operation', operationId, 'users']);
            }
        });
};


export const useLot = (lotId, isCleTransverse = false) => {
    return useQuery<Lot>(
        ["lot", lotId, isCleTransverse],
        () => ApiHelper.getLot(lotId, isCleTransverse).then(res => res.data),
        {
            enabled: !!lotId
            // placeholderData: []
        }
    );
};

export const useReserve = (reserveId) => {
    return useQuery<Reserve>(
        ["reserve", reserveId],
        () => ApiHelper.getReserve(reserveId).then(res => res.data),
        {
            enabled: reserveId !== 'new' && !isNaN(reserveId)
            // placeholderData: []
        }
    );
};

export const useLotReserves = (lotId) => {
    return useQuery<Reserve[]>(
        ["lot", lotId, "reserves"],
        () => ApiHelper.getLotReserves(lotId).then(res => res.data),
        {
            enabled: !isNaN(lotId)
            // placeholderData: []
        }
    );
};

export const useUsers = (activeFilter: boolean) => {
  return useQuery<User[]>(
    ["user", activeFilter, "active"],
    () =>
      ApiHelper.getUsers({
        indicActif: activeFilter ? "true" : "false", // <- string explicite
      }).then((res) => res.data)
  );
};


export const useUser = (userId, isMe) => {
    const alert = useAlert();

    return useQuery<User>(
        ["user", isMe ? 'me' : userId],
        () => {
            const getUserPromise = isMe ? ApiHelper.getCurrentUser() : ApiHelper.getUser(userId);

            return getUserPromise.then(res => res.data).catch((error: AxiosError) => {

                if (error) {
                    alert.error('Not Found');
                }

                throw error;
            });
        },
        {
            enabled: !isNaN(userId) || isMe
        }
    );
};

export const useUpdateUser = (isMe: boolean) => {
    const queryClient = useQueryClient();
    const { updateUser } = useContext(AuthContext);

    return useMutation<AxiosResponse<User>, AxiosError, { userId?: number | string, values: any }>(
        // ['user'],
        ({userId, values}) => {
            return userId ? ApiHelper.patchUser(userId, values) : ApiHelper.postUser(values);
        },
        {
            onSuccess: async (result, variables) => {
                const {data: newUser} = result;
                const {userId} = variables;

                if (isMe) {
                    updateUser(newUser);
                }

                if (queryClient.getQueryData(['user', true, 'active'])) {
                    queryClient.setQueryData(['user', true, 'active'], (oldUsers: User[]) => {
                        if (!oldUsers)
                            return;
                        const newUsers = oldUsers.filter((user) => user.refUti !== newUser.refUti);

                        if (newUser.indicActif === true) {
                            newUsers.push(newUser);
                        }

                        return newUsers;
                    });
                }

                if (queryClient.getQueryData(['user', false, 'active'])) {
                    queryClient.setQueryData(['user', false, 'active'], (oldUsers: User[]) => {
                        if (!oldUsers)
                            return;

                        const newUsers = oldUsers.filter((user) => user.refUti !== newUser.refUti);

                        if (newUser.indicActif === false) {
                            newUsers.push(newUser);
                        }

                        return newUsers;
                    });
                }

                await queryClient.invalidateQueries(['user', isMe ? 'me' : userId]);
            }
        });
};

export const useUpdateReservesStatus = () => {
    const queryClient = useQueryClient();

    return useMutation<AxiosResponse<Reserve[]>, AxiosError, { reserveIds: string[], statusId: number }>(
        // ['user'],
        ({reserveIds, statusId}) => {
            return ApiHelper.updateReservesStatus(reserveIds, statusId);
        },
        {
            onSuccess: async (result) => {
                const {data: reserves} = result;
                const operationId = reserves[0].lot.operationRefOperation;
                const lotIds = reserves.reduce((ids, r) => {
                    if (!ids.includes(r.lotRefLot)) {
                        return ids.concat(r.lotRefLot);
                    }
                    return ids;
                }, []);

                queryClient.setQueryData(["operation", operationId, "reserves"],
                    (oldReserves: Reserve[]) => oldReserves?.map((reserve) => {
                        const newReserveData = reserves.find(r => r.refReserve === reserve.refReserve);

                        if (!newReserveData) {
                            return reserve;
                        }

                        return {
                            ...reserve,
                            statut: newReserveData.statut,
                            statutRefStatut: newReserveData.statutRefStatut,
                            dateLevee: newReserveData.dateLevee,
                            modifParRef: newReserveData.modifParRef,
                            modifPar: newReserveData.modifPar,
                            dateModif: newReserveData.dateModif,
                        };
                    }));
                await Promise.all(reserves
                    .filter(reserve => queryClient.getQueryState(['reserve', reserve.refReserve])?.status === 'success')
                    .map((reserve) => queryClient.refetchQueries(['reserve', reserve.refReserve]))
                );

                lotIds.forEach((lotId) => {
                    queryClient.setQueryData(["lot", Number(lotId), "reserves"],
                        (oldReserves: Reserve[]) => oldReserves?.map((reserve) => {
                            const newReserveData = reserves.find(r => r.refReserve === reserve.refReserve);

                            if (!newReserveData) {
                                return reserve;
                            }

                            return {
                                ...reserve,
                                statut: newReserveData.statut,
                                statutRefStatut: newReserveData.statutRefStatut,
                                dateLevee: newReserveData.dateLevee,
                                modifParRef: newReserveData.modifParRef,
                                modifPar: newReserveData.modifPar,
                                dateModif: newReserveData.dateModif,
                            };
                        }));
                });
            }
        });
};

export const useApiDocs = () => {
    return useQuery<string>(
        ["api-docs"],
        () => ApiHelper.getApiDocs().then(res => res.data)
    );
};

export const useMessages = () => {
    return useQuery<Message[]>(
        ["message"],
        () => ApiHelper.getMessages().then(res => res.data)
    );
};

export const useMessage = (appVersionId) => {
    return useQuery<Message>(
        ["message", appVersionId],
        () => ApiHelper.getMessage(appVersionId).then(res => res.data),
        {
            enabled: !isNaN(appVersionId)
        }
    );
};

export const useLastMessage = () => {
    return useQuery<Message>(
        ["message", "latest"],
        () => ApiHelper.getLastMessage().then(res => res.data ? res.data : undefined),
        {}
    );
};

export const useUpdateMessage = () => {
    const queryClient = useQueryClient();

    return useMutation<AxiosResponse<Message>, AxiosError, { messageId?: number | string, values: Partial<Message> }>(
        ({messageId, values}) => {
            return messageId ? ApiHelper.patchMessage(messageId, values) : ApiHelper.postMessage(values);
        },
        {
            onSuccess: async (result) => {
                await queryClient.refetchQueries(['message']);
            }
        });
};

export const useAppVersions = () => {
    return useQuery<AppVersion[]>(
        ["app-version"],
        () => ApiHelper.getAppVersions().then(res => res.data)
    );
};

export const useAppVersion = (appVersionId) => {
    return useQuery<AppVersion>(
        ["app-version", appVersionId],
        () => ApiHelper.getAppVersion(appVersionId).then(res => res.data),
        {
            enabled: !isNaN(appVersionId)
        }
    );
};

export const useLastAppVersion = () => {
    return useQuery<AppVersion>(
        ["app-version", "latest"],
        () => ApiHelper.getLastAppVersion().then(res => res.data ? res.data : undefined),
        {}
    );
};

export const useUpdateAppVersion = () => {
    const queryClient = useQueryClient();

    return useMutation<AxiosResponse<AppVersion>, AxiosError, { appVersionId?: number | string, formData: FormData }>(
        ({appVersionId, formData}) => {
            return appVersionId ? ApiHelper.patchAppVersion(appVersionId, formData) : ApiHelper.postAppVersion(formData);
        },
        {
            onSuccess: async (result) => {
                await queryClient.refetchQueries(['app-version']);
            }
        });
};

export const useUpdateReserve = () => {
    const queryClient = useQueryClient();

    return useMutation<Reserve, AxiosError, { reserveId?: string, values: Partial<Reserve>, photoFile: File, docFile?: File, updateReserve: boolean, operationId: number }>(
        ({reserveId, values, photoFile, docFile, updateReserve}) => {
            return (updateReserve ? ApiHelper.patchReserve(reserveId, values) : ApiHelper.postReserve(values)).then((response) => {

                const newReserve = response.data;

                if (photoFile) {
                    return ApiHelper.postReservePhoto(newReserve.refReserve, photoFile).then(() => newReserve);
                }

                return newReserve;
            }).then((newReserve) => {
                if (docFile) {
                    return ApiHelper.postReserveDocLevee(newReserve.refReserve, docFile).then(() => newReserve);
                }

                return newReserve;
            });
        },
        {
            onSuccess: async (newReserve, variables) => {
                const {operationId} = variables;

                await queryClient.refetchQueries(['reserve', newReserve.refReserve]);
                await queryClient.refetchQueries(["operation", operationId, "reserves"]);
                await queryClient.refetchQueries(["lot", newReserve.lotRefLot, "reserves"]);
            }
        });
};

export const useUpdateLot = () => {
    const queryClient = useQueryClient();

    return useMutation<AxiosResponse<OperationElementAnnexe[]>, AxiosError, { lot: Lot }>(
        ({lot}) => {
            return ApiHelper.patchLot(lot.refLot, lot);
        },
        {
            onSuccess: async (result, variables) => {
                const {lot} = variables;

                await queryClient.refetchQueries(["operation", lot.operationRefOperation, "lots"]);
                await queryClient.refetchQueries(["lot", lot.refLot]);
            }
        });
};

export const useUpdateOperationElements = () => {
    const queryClient = useQueryClient();

    return useMutation<AxiosResponse<OperationElementAnnexe[]>, AxiosError, { operationId: number, opAnnexElements: OperationElementAnnexe[], withUpdate: boolean }>(
        // ['user'],
        ({operationId, opAnnexElements, withUpdate = false}) => {
            return ApiHelper.postOperationElements(operationId, opAnnexElements).then(async res => {
                if (withUpdate) {
                    return ApiHelper.updateOperationLotsMissingAdditionalElements(operationId).then(resUpdate => {
                        return res;
                    });
                }

                return res;
            });
        },
        {
            onSuccess: (result) => {
                const {data: opAnnexElements} = result;

                if (opAnnexElements.length) {
                    const operationId = opAnnexElements[0].operationRefOperation;
                    queryClient.setQueryData(["operation", operationId, "elements"], opAnnexElements);
                }
            }
        });
};


export const useThrottledEffect = (callback, delay, deps = []) => {
    const lastRan = useRef(Date.now());

    useEffect(
        () => {
            const handler = setTimeout(function () {
                if (Date.now() - lastRan.current >= delay) {
                    callback();
                    lastRan.current = Date.now();
                }
            }, delay - (Date.now() - lastRan.current));

            return () => {
                clearTimeout(handler);
            };
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [delay, ...deps],
    );
};

export const useThrottledLayoutEffect = (callback, delay, deps = []) => {
    const lastRan = useRef(Date.now());

    useLayoutEffect(
        () => {
            const handler = setTimeout(function () {
                if (Date.now() - lastRan.current >= delay) {
                    callback();
                    lastRan.current = Date.now();
                }
            }, delay - (Date.now() - lastRan.current));

            return () => {
                clearTimeout(handler);
            };
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [delay, ...deps],
    );
};

export default useThrottledEffect;

const usePrevious = (value, initialValue) => {
    const ref = useRef(initialValue);
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
};

export const useEffectDebugger = (effectHook, dependencies, dependencyNames = []) => {
    const previousDeps = usePrevious(dependencies, []);

    const changedDeps = dependencies.reduce((accum, dependency, index) => {
        if (dependency !== previousDeps[index]) {
            const keyName = dependencyNames[index] || index;
            return {
                ...accum,
                [keyName]: {
                    before: previousDeps[index],
                    after: dependency
                }
            };
        }

        return accum;
    }, {});

    if (Object.keys(changedDeps).length) {
        console.log('[use-effect-debugger] ', changedDeps);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(effectHook, dependencies);
};

/**
 * Hook that alerts clicks outside of the passed ref
 */
export const useOutsideAlerter = (ref, action) => {
    useEffect(() => {
        /**
         * Alert if clicked on outside of element
         */
        function handleClickOutside(event) {
            if (ref.current && !ref.current.contains(event.target)) {

                if (action) {
                    action();
                }
            }
        }

        // Bind the event listener
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener("mousedown", handleClickOutside);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref]);
}

export const useDebounce = (value, delay) => {
    // State and setters for debounced value
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(
        () => {
            // Update debounced value after delay
            const handler = setTimeout(() => {
                setDebouncedValue(value);
            }, delay);
            // Cancel the timeout if value changes (also on delay change or unmount)
            // This is how we prevent debounced value from updating if value is changed ...
            // .. within the delay period. Timeout gets cleared and restarted.
            return () => {
                clearTimeout(handler);
            };
        },
        [value, delay] // Only re-call effect if value or delay changes
    );

    return debouncedValue;
}
